// src/components/Footer.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Link,
} from "@mui/material";
import { motion } from "framer-motion";

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";
import { useFrustrationSensor } from "./Shortener/hooks/useFrustrationSensor";

// --- Framer Motion Components ---
// Wrapping MUI Link to accept framer-motion props safely without console errors
const MotionLink = motion(Link);

const Footer = () => {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  useFrustrationSensor(); // 🧠 Watch for frustration at the bottom of the page

  const { t, i18n } = useTranslation();

  // 🧠 AI Actuator: Simplify UI if cognitive load is high
  const shouldSimplify = agentState.ui.simplifyAnimations;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // 🧠 AI Sensor: Track localization preferences
    reportBehavior(`language_changed_${lng}`, 3, 'behavior');
  };

  // Framer Motion Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <motion.footer
      initial={shouldSimplify ? "visible" : "hidden"}
      animate="visible"
      className={agentState.theme} // 🧠 Apply Dynamic AI Theme
      style={{
        background: "linear-gradient(180deg, #111318, #0b0c0f)",
        backgroundSize: "400% 400%",
        // Kill background animation if user is frustrated
        animation: shouldSimplify ? "none" : "gradientAnimation 15s ease infinite",
        color: "#ffffff",
        paddingTop: 48,
        paddingBottom: 48,
      }}
    >
      <Container maxWidth="lg">
        {/* Language Switcher */}
        <motion.div variants={fadeInUp}>
          <Box display="flex" justifyContent="flex-end" mb={4}>
            <FormControl sx={{ minWidth: 140 }} size="small">
              <Select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                sx={{
                  bgcolor: "grey.800",
                  color: "common.white",
                  ".MuiSvgIcon-root": { color: "common.white" },
                }}
              >
                <MenuItem value="en">🇬🇧 English</MenuItem>
                <MenuItem value="si">🇱🇰 Sinhala</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Video Editor Global
              </Typography>
              <Typography variant="body2" color="grey.400">
                {t("footer.companyDescription")}
              </Typography>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.quickLinks")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[
                  { label: t("home1"), path: "/" },
                  { label: t("about1"), path: "/about" },
                  { label: t("features1"), path: "/features" },
                  { label: t("footer.dashboard"), path: "/dashboard" },
                  { label: t("contact1"), path: "/contact" },
                ].map((link) => (
                  <MotionLink
                    component={RouterLink}
                    to={link.path}
                    color="grey.400"
                    underline="hover"
                    key={link.path}
                    // 🧠 AI Sensor: Track exactly what infrastructure component they care about
                    onClick={() => reportBehavior(`intent_nav_${link.path.slice(1) || "home"}`, 2, 'interest')}
                    whileHover={!shouldSimplify ? { color: "#d7b879", x: 5 } : {}}
                    transition={{ type: "tween", duration: 0.2 }}
                  >
                    {link.label}
                  </MotionLink>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.resources")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[
                  { label: t("features1"), path: "/features", key: "features" },
                  { label: t("footer.support"), path: "/contact", key: "support" },
                ].map((res) => (
                  <MotionLink
                    component={RouterLink}
                    to={res.path}
                    color="grey.400"
                    underline="hover"
                    key={res.key}
                    // 🧠 AI Sensor: Track technical/support intent
                    onClick={() => reportBehavior(`intent_resource_${res.key}`, 3, 'interest')}
                    whileHover={!shouldSimplify ? { color: "#d7b879", x: 5 } : {}}
                    transition={{ type: "tween", duration: 0.2 }}
                  >
                    {res.label}
                  </MotionLink>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Contact & Social */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.connect")}
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                {/* Add SVG social icons with hover animations */}
              </Box>
              <Typography variant="body2" color="grey.400">
                Email:{" "}
                <a 
                    href="mailto:unicornprofessionalbay@gmail.com" 
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={() => reportBehavior('direct_email_footer', 5, 'interest')}
                >
                    unicornprofessionalbay@gmail.com
                </a>
              </Typography>
              <Typography variant="body2" color="grey.400">
                Phone:{" "}
                <a 
                    href="tel:+94750788368" 
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={() => reportBehavior('direct_phone_footer', 5, 'interest')}
                >
                    +94-75-0788368
                </a>
              </Typography>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ bgcolor: "grey.700", my: 4 }} />
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="grey.400">
            © {new Date().getFullYear()} Video Editor Global {t("footer.rights")}
          </Typography>
          <Box display="flex" gap={3} mt={{ xs: 2, md: 0 }}>
            {["privacy", "terms", "cookies"].map((link) => (
              <MotionLink
                component={RouterLink}
                to={`/${link}`}
                color="grey.400"
                underline="hover"
                key={link}
                onClick={() => reportBehavior(`intent_legal_${link}`, 1, 'interest')}
                whileHover={!shouldSimplify ? { color: "#d7b879", scale: 1.05 } : {}}
              >
                {t(`footer.${link}`)}
              </MotionLink>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Animated Gradient Keyframes */}
      <style>
        {`
          @keyframes gradientAnimation {
            0% {background-position: 0% 50%;}
            50% {background-position: 100% 50%;}
            100% {background-position: 0% 50%;}
          }
        `}
      </style>
    </motion.footer>
  );
};

export default Footer;